import { formatDateTime } from '@pickleballinc/lib/format-dates';
import moment from 'moment';

import {
  TOURNAMENT_DIVISION_TYPES,
  EMPTY_COLOR_PRIMARY,
  LIGHTEN_FACTOR,
  WHITE_COLOR,
  PROFILE_DIVISION_TYPES_WITH_WORLD,
} from '@/modules/apr/profile/constants';
import { DataType, GaugeVariant, StickyHeaderPlayerData } from '@/modules/apr/profile/types';
import { convertFeetAndInchesToCentimeters } from '@/modules/players/v3/utils/edit-profile';
import {
  FormattedAPRRankingProfileData,
  RenderTournamentEventData,
  RenderTournamentGroupedEventData,
  PlayerProfileData,
} from '@/types/aprRankings.types';
import { PlayerData } from '@/types/players.types';

export const getPlays = (plays: string) => {
  if (plays === 'RT') {
    return 'Right-handed';
  } else if (plays === 'LT') {
    return 'Left-handed';
  }
  return '-';
};

export const getGender = (gender: string) => {
  if (gender === 'M') {
    return 'Male';
  } else if (gender === 'F') {
    return 'Female';
  }
  return '-';
};

export const formatAge = (age?: string) => {
  if (!age) return '--';
  return `${age} yrs`;
};

export const formatHeight = (feet?: string, inches?: string) => {
  if (!feet || !inches) return '--';
  return `${feet}' ${inches}" - ${convertFeetAndInchesToCentimeters(+feet, +inches)} cm`;
};

export const formatRank = (rank?: string) => {
  return rank ? `#${rank}` : 'NR';
};

export const getOrdinalSuffix = (placement: string): string => {
  const num = Number(placement);
  if (isNaN(num)) return '';
  const remainder10 = num % 10;
  const remainder100 = num % 100;

  if (remainder100 >= 11 && remainder100 <= 13) return 'th';

  switch (remainder10) {
    case 1:
      return 'st';
    case 2:
      return 'nd';
    case 3:
      return 'rd';
    default:
      return 'th';
  }
};

export const getPlayerDataForRankHistory = (
  playerData?: PlayerData
): StickyHeaderPlayerData | null => {
  if (!playerData) return null;

  const profileImage = playerData.images.find((image) => image.type === 'profileImg');

  const playerStickyData: StickyHeaderPlayerData = {
    firstName: playerData.firstName,
    lastName: playerData.lastNameAndSuffix,
    image: profileImage?.url || '',
    country: playerData.country,
    playerLevelId: playerData.typeOfPlayer || 'Amateur',
    countryFlag: playerData.countryFlag,
  };

  return playerStickyData;
};

export const getPlayerDataForProfile = (
  profileData?: PlayerProfileData | FormattedAPRRankingProfileData
): StickyHeaderPlayerData | null => {
  if (!profileData) return null;

  const playerStickyData: StickyHeaderPlayerData = {
    firstName: profileData.firstName,
    lastName: profileData.lastName,
    image: profileData?.playerImage || '',
    country: profileData.abbreviation,
    playerLevelId: profileData.playerLevelId || 'Amateur',
    countryFlag: profileData.countryFlag,
  };

  return playerStickyData;
};

export const groupTournamentEvents = (events: RenderTournamentEventData[]) => {
  const mapOfGroupedEvents = new Map<string, RenderTournamentGroupedEventData[]>();
  events.forEach((event) => {
    const bracketLevel = event.bracketLevel;
    const divisionType = event.eventDivisionType;

    const divisionLabel = TOURNAMENT_DIVISION_TYPES[divisionType];
    if (!divisionLabel) return;

    const header = `${divisionLabel} ${bracketLevel}`;
    const key = `${bracketLevel}__${divisionType}`;

    const existing = mapOfGroupedEvents.get(key) ?? [];

    const groupedEvent: RenderTournamentGroupedEventData = {
      header,
      bracketLevel,
      divisionType,
      event,
    };

    mapOfGroupedEvents.set(key, [...existing, groupedEvent]);
  });

  return mapOfGroupedEvents;
};

const hexToRgb = (hex: string): [number, number, number] => {
  const cleanHex = hex.replace('#', '');
  const r = parseInt(cleanHex.substring(0, 2), 16);
  const g = parseInt(cleanHex.substring(2, 4), 16);
  const b = parseInt(cleanHex.substring(4, 6), 16);
  return [r, g, b];
};

const rgbToHex = (r: number, g: number, b: number): string => {
  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
};

export const calculateGradientColor = (
  startRgb: [number, number, number],
  endRgb: [number, number, number],
  ratio: number
): string => {
  const [r1, g1, b1] = startRgb;
  const [r2, g2, b2] = endRgb;
  const r = Math.round(r1 + (r2 - r1) * ratio);
  const g = Math.round(g1 + (g2 - g1) * ratio);
  const b = Math.round(b1 + (b2 - b1) * ratio);
  return rgbToHex(r, g, b);
};

export const generateGaugeColors = (
  filledLevels: number,
  totalLevels: number,
  dataType: DataType,
  variant: GaugeVariant,
  themeColor: string
): string[] => {
  const baseColor = variant === 'primary' ? themeColor : WHITE_COLOR;
  const emptyColor = variant === 'primary' ? EMPTY_COLOR_PRIMARY : themeColor;

  const [baseR, baseG, baseB] = hexToRgb(baseColor);
  const lightR = Math.round(baseR + (255 - baseR) * LIGHTEN_FACTOR);
  const lightG = Math.round(baseG + (255 - baseG) * LIGHTEN_FACTOR);
  const lightB = Math.round(baseB + (255 - baseB) * LIGHTEN_FACTOR);

  if (dataType === 'player') {
    return Array(totalLevels)
      .fill(null)
      .map((_, index) => {
        if (index < filledLevels) {
          const ratio = filledLevels > 1 ? index / (filledLevels - 1) : 0;
          return calculateGradientColor([lightR, lightG, lightB], [baseR, baseG, baseB], ratio);
        }
        return emptyColor;
      });
  }
  return Array(totalLevels)
    .fill(null)
    .map((_, index) => {
      const ratio = index / (totalLevels - 1);
      return calculateGradientColor([lightR, lightG, lightB], [baseR, baseG, baseB], ratio);
    });
};

export const calculatePercent = (
  currentPoints: number,
  pointsHighLimit: number,
  inverted: boolean,
  percentage?: number
) => {
  if (percentage) {
    return inverted ? 1 - percentage / 100 : percentage / 100;
  }
  const rawPercent = Math.max(0, Math.min(1, currentPoints / pointsHighLimit));
  return inverted ? 1 - rawPercent : rawPercent;
};

export const isEmptyGaugeValue = (gaugeValue?: string | null) => !gaugeValue || gaugeValue === '0';

export const getWeeks52DateRange = () => {
  const weeks52Start = formatDateTime(moment().subtract(1, 'year').add(1, 'day').toISOString());
  const weeks52End = formatDateTime(moment().toISOString());

  return { weeks52Start, weeks52End };
};

export const getRaceDateRange = (raceFilterYear?: string | number) => {
  const isCurrentYear = raceFilterYear ? +raceFilterYear === moment().year() : true;
  const raceStart = isCurrentYear
    ? formatDateTime(moment().startOf('year').toISOString())
    : formatDateTime(moment(`${raceFilterYear}-01-01`).toISOString());
  const raceEnd = isCurrentYear
    ? formatDateTime(moment().toISOString())
    : formatDateTime(moment(`${raceFilterYear}-12-31`).toISOString());

  return { raceStart, raceEnd };
};

export function getDivisionTabTitle(divisionType: string | null, gender: string): string {
  if (divisionType === null) return 'World Ranking';
  const divisionObject = PROFILE_DIVISION_TYPES_WITH_WORLD.find((divisionTypeObject) => {
    const currentType =
      gender === 'Female' ? divisionTypeObject.femaleType : divisionTypeObject.maleType;
    return currentType === divisionType;
  });
  return divisionObject?.title ?? 'World Ranking';
}
